import React from 'react';
import { TabProps } from './types';
import { getSpeedBadge } from './utils';

const TimingTab: React.FC<TabProps> = ({
  timingAnalysis,
  azureAnalysis,
}) => {
  if (!timingAnalysis) return null;

  return (
    <div>
      {/* Overall timing stats */}
      <div className={`grid ${azureAnalysis ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-4'} gap-4 mb-6`}>
        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">
            {timingAnalysis.totalDurationSec}s
          </div>
          <div className="text-xs text-purple-500">Total Duration</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {timingAnalysis.wordsPerMinute}
          </div>
          <div className="text-xs text-blue-500">Words/Minute</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {timingAnalysis.fluencyScore}%
          </div>
          <div className="text-xs text-green-500">Fluency Score</div>
        </div>
        {!azureAnalysis && timingAnalysis.pauses && (
          <div className="bg-orange-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">
              {timingAnalysis.pauses.length}
            </div>
            <div className="text-xs text-orange-500">Pauses Detected</div>
          </div>
        )}
      </div>

      {/* Pace comparison */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-gray-700 mb-2">Speaking Pace</h4>
        <div className="flex items-center gap-4">
          <div className="flex-1 bg-gray-200 rounded-full h-3 relative">
            <div
              className="absolute h-3 bg-purple-500 rounded-full"
              style={{
                width: `${Math.min(100, (timingAnalysis.wordsPerMinute / 200) * 100)}%`
              }}
            />
            <div
              className="absolute w-1 h-5 bg-green-500 -top-1"
              style={{ left: `${(150 / 200) * 100}%` }}
              title="Native speaker average"
            />
          </div>
          {!azureAnalysis && timingAnalysis.paceComparison && (
            <span className="text-sm text-gray-600 whitespace-nowrap">
              {timingAnalysis.paceComparison}
            </span>
          )}
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>0 WPM</span>
          <span className="text-green-600">Native: ~150 WPM</span>
          <span>200 WPM</span>
        </div>
      </div>

      {/* Azure fluency feedback */}
      {azureAnalysis && (
        <div className="bg-purple-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-purple-700 mb-2">Fluency Analysis</h4>
          <p className="text-purple-600">
            {azureAnalysis.fluency >= 80
              ? 'Excellent! Your speech flows naturally with good rhythm.'
              : azureAnalysis.fluency >= 60
              ? 'Good pace, but try to reduce pauses between words for smoother speech.'
              : 'Work on speaking more smoothly with fewer hesitations between words.'}
          </p>
        </div>
      )}

      {/* Word-by-word timing (only for non-Azure analysis) */}
      {!azureAnalysis && timingAnalysis.wordTimings && (
        <>
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Word Timing Breakdown
          </h4>
          <div className="space-y-2">
            {timingAnalysis.wordTimings.map((wt, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <span className="font-medium">{wt.word}</span>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-500">{wt.durationMs}</span>
                  <span className={`text-sm font-medium ${getSpeedBadge(wt.speed)}`}>
                    {wt.speed}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pauses (only for non-Azure analysis) */}
      {!azureAnalysis && timingAnalysis.pauses && timingAnalysis.pauses.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Pauses Detected
          </h4>
          <div className="space-y-2">
            {timingAnalysis.pauses.map((pause, index) => (
              <div
                key={index}
                className={`
                  p-3 rounded-lg flex items-center justify-between
                  ${pause.type === 'long' ? 'bg-red-50' :
                    pause.type === 'medium' ? 'bg-yellow-50' : 'bg-gray-50'}
                `}
              >
                <span className="text-sm">
                  After "<span className="font-medium">{pause.afterWord}</span>"
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">{pause.duration}ms</span>
                  <span className={`
                    text-xs px-2 py-0.5 rounded
                    ${pause.type === 'long' ? 'bg-red-100 text-red-700' :
                      pause.type === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'}
                  `}>
                    {pause.type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimingTab;
